import { streamingPost } from '@/utils/streamingUtils';
import { buildFetchConfig } from '@/utils/fetchUtils';
import {
  extractAIMessageContent,
  extractStreamContent,
  extractStreamingReasoningStep,
  processChartConfig,
  processScriptFromToolMessage,
  processDrainDetectionFromToolMessage,
  extractAllReasoningSteps,
  calculateReasoningStepsHash,
} from '@/utils/chatUtils';
import type {
  ChatMessage,
  ReasoningStep,
  ContentBlock,
  PromptRequest,
  FeedbackParams,
} from '@Types/chat';

export const sendPrompt = async (
  request: PromptRequest,
  onChunk?: (
    content: string,
    extras?: ChatMessage['extras'],
    reasoningSteps?: ReasoningStep[],
  ) => void,
  signal?: AbortSignal,
): Promise<ChatMessage> => {
  const { ...requestBody } = request;
  let allMessages: Array<any> = [];
  let finalExtras: any = {};
  let lastContent = '';
  let lastChartCount = 0;
  let lastReasoningStepsHash = '';
  let lastDrainDetectionId = '';
  let streamedContent = '';
  // Track reasoning content per iteration (each AIMessage with tools = one iteration)
  const reasoningContentByIteration: string[] = [];
  let currentIterationContent = '';
  let lastAiMessageWithToolsCount = 0;
  const streamingReasoningSteps: ReasoningStep[] = [];

  await streamingPost(
    '/prompt',
    requestBody,
    (line: string) => {
      try {
        const chunk = JSON.parse(line);

        if (chunk.extras) {
          finalExtras = chunk.extras;
        }

        // Accumulate reasoning content for current iteration
        const reasoningChunk =
          chunk?.type === 'content_stream' &&
          chunk.content &&
          chunk.is_reasoning
            ? chunk.content
            : '';
        if (reasoningChunk) {
          currentIterationContent += reasoningChunk;
        }

        if (chunk.messages?.length) {
          allMessages = chunk.messages;

          // Count AIMessages with tool_calls to detect new iterations
          const aiMessagesWithTools = allMessages.filter(
            (msg: any) =>
              msg?.id?.[msg.id.length - 1] === 'AIMessage' &&
              msg.kwargs?.tool_calls?.length > 0,
          );

          // If we have a new AIMessage with tools, save current reasoning content
          if (aiMessagesWithTools.length > lastAiMessageWithToolsCount) {
            if (currentIterationContent.trim()) {
              reasoningContentByIteration.push(currentIterationContent.trim());
            }
            currentIterationContent = '';
            lastAiMessageWithToolsCount = aiMessagesWithTools.length;
          }
        }

        // Route final content to text block
        streamedContent += extractStreamContent(chunk); // is_final -> text block

        const streamingStep = extractStreamingReasoningStep(chunk);
        if (streamingStep) {
          streamingReasoningSteps.push(streamingStep);
        }

        const curveConfig = processChartConfig(allMessages);
        const scriptData = processScriptFromToolMessage(allMessages);
        const drainDetection =
          processDrainDetectionFromToolMessage(allMessages);

        // Build reasoning steps: tool info from messages + streamed content per iteration + tool_logs
        const messageReasoningSteps = extractAllReasoningSteps(allMessages);

        // Inject reasoning content into tool steps using a separate iteration counter
        let iterationIdx = 0;
        const toolStepCount = messageReasoningSteps.filter(
          s => s.tools && s.tools.length > 0,
        ).length;
        const reasoningStepsWithContent = messageReasoningSteps.map(step => {
          if (step.tools && step.tools.length > 0) {
            const savedContent = reasoningContentByIteration[iterationIdx];
            const isLastToolStep = iterationIdx === toolStepCount - 1;
            const contentToUse =
              savedContent ||
              (isLastToolStep ? currentIterationContent.trim() : '');

            iterationIdx++;
            if (contentToUse) {
              return { ...step, content: contentToUse };
            }
          }
          return step;
        });

        const reasoningSteps = [
          ...reasoningStepsWithContent,
          ...streamingReasoningSteps,
        ];

        const contentChanged =
          streamedContent !== lastContent && streamedContent.trim().length > 0;
        const chartCount = curveConfig
          ? Array.isArray(curveConfig)
            ? curveConfig.length
            : 1
          : 0;
        const chartsChanged = chartCount > lastChartCount;
        const drainDetectionChanged =
          drainDetection?.detection_id &&
          drainDetection.detection_id !== lastDrainDetectionId;

        const { reasoningStepsHash, reasoningStepsChanged } =
          calculateReasoningStepsHash(reasoningSteps, lastReasoningStepsHash);

        if (
          (contentChanged ||
            chartsChanged ||
            reasoningStepsChanged ||
            drainDetectionChanged) &&
          onChunk
        ) {
          lastContent = streamedContent;
          lastChartCount = chartCount;
          lastReasoningStepsHash = reasoningStepsHash;
          if (drainDetection?.detection_id) {
            lastDrainDetectionId = drainDetection.detection_id;
          }
          onChunk(
            streamedContent,
            {
              ...finalExtras,
              ...(curveConfig && { curveConfig }),
              ...(scriptData && { scriptData }),
              ...(drainDetection && { drainDetection }),
            },
            reasoningSteps,
          );
        }
      } catch (e) {
        console.error('[STREAMING] Error parsing chunk:', e);
        console.error('[STREAMING] Failed chunk data:', line);
      }
    },
    undefined,
    signal,
  );

  const finalContent = extractAIMessageContent(allMessages);
  if (!finalContent) throw new Error('No AI response content found');
  const finalCurveConfig = processChartConfig(allMessages);
  const finalScriptData = processScriptFromToolMessage(allMessages);
  const finalDrainDetection = processDrainDetectionFromToolMessage(allMessages);
  const extractedReasoningSteps = extractAllReasoningSteps(allMessages);

  let finalIterationIdx = 0;
  const finalReasoningSteps = extractedReasoningSteps.map(step => {
    if (step.tools && step.tools.length > 0) {
      const savedContent = reasoningContentByIteration[finalIterationIdx];
      finalIterationIdx++;
      if (savedContent) {
        return { ...step, content: savedContent };
      }
    }
    return step;
  });

  const blocks: ContentBlock[] = [];
  if (finalScriptData) {
    blocks.push({ type: 'code', data: finalScriptData });
  }
  if (finalCurveConfig) {
    blocks.push({ type: 'charts', data: finalCurveConfig });
  }
  if (finalDrainDetection) {
    blocks.push({ type: 'drain_detection', data: finalDrainDetection });
  }
  if (finalContent.trim()) {
    blocks.push({ type: 'text', data: finalContent });
  }

  return {
    role: 'assistant',
    content: finalContent,
    timestamp: new Date().toISOString(),
    blocks: blocks.length ? blocks : undefined,
    reasoningSteps: finalReasoningSteps.length
      ? finalReasoningSteps
      : undefined,
    extras: {
      ...finalExtras,
      ...(finalCurveConfig && { curveConfig: finalCurveConfig }),
      ...(finalScriptData && { scriptData: finalScriptData }),
      ...(finalDrainDetection && { drainDetection: finalDrainDetection }),
    },
    messageCompleted: true,
  };
};

export const sendFeedback = async (
  feedback: FeedbackParams,
): Promise<string> => {
  const { baseURL, headers } = buildFetchConfig();
  try {
    await fetch(`${baseURL}/feedbacks`, {
      method: 'POST',
      headers,
      body: JSON.stringify(feedback),
    });

    return 'Thanks for your feedback!';
  } catch (error) {
    console.error('[FEEDBACK] Network error:', error);
    return 'Failed to submit feedback. Please check your connection and try again.';
  }
};
